import {
  PointerEvent,
  TouchEvent,
  useEffect,
  useRef,
  useState,
} from "react"
import { useIsMountedRef } from "shared/hooks/useIsMounted"
import { clearTimeout, Timeout } from "shared/utils/timeout"

interface DesktopControlsOverlayProps {
  controlsEnabled: boolean
  isPlaying: boolean
  hideDelayMs: number
}

// Detects mouseover/touch via returned ref & event handlers
// Also returns a boolean, representing whether to show/hide the controls overlay
export const useDesktopControlsOverlay = ({
  controlsEnabled,
  isPlaying,
  hideDelayMs,
}: DesktopControlsOverlayProps) => {
  const [showControlsOverlay, setShowControlsOverlay] = useState(false)
  const hideTimeoutRef = useRef<Timeout | null>(null)
  const isMountedRef = useIsMountedRef()

  const pointerDetectionAreaRef = useRef<HTMLDivElement | null>(null)

  const hideElementsAfterDelay = () => {
    clearTimeout(hideTimeoutRef.current)

    hideTimeoutRef.current = setTimeout(() => {
      if (isMountedRef) {
        setShowControlsOverlay(false)
        pointerDetectionAreaRef.current?.blur()
      }
    }, hideDelayMs)
  }

  useEffect(() => {
    return () => {
      clearTimeout(hideTimeoutRef.current)
    }
  }, [])

  // Show hideable elements whenever the video starts playing
  useEffect(() => {
    if (isPlaying) {
      setShowControlsOverlay(true)
      clearTimeout(hideTimeoutRef.current)
      hideElementsAfterDelay()
    }
  }, [isPlaying])

  // Show hideable elements whenever paused
  useEffect(() => {
    if (!isPlaying) {
      setShowControlsOverlay(true)
      clearTimeout(hideTimeoutRef.current)
    } else {
      hideElementsAfterDelay()
    }
  }, [isPlaying])

  // Show hideable elements for some time after the cursor is moved
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse" && isPlaying) {
      setShowControlsOverlay(true)
      hideElementsAfterDelay()
    }
  }

  // Hide elements when the cursor leaves
  const onPointerLeave = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse" && isPlaying) {
      clearTimeout(hideTimeoutRef.current)
      setShowControlsOverlay(false)
    }
  }

  const onTouch = (event: TouchEvent<HTMLDivElement>) => {
    if (!isPlaying) {
      return
    }
    // If already showing hideable elements, hide them
    if (
      showControlsOverlay &&
      event.target === pointerDetectionAreaRef.current
    ) {
      setShowControlsOverlay(false)
    } else {
      // Otherwise, show elements for some time
      setShowControlsOverlay(true)
      hideElementsAfterDelay()
    }
  }

  return {
    pointerDetectionAreaRef,
    onPointerLeave,
    onPointerMove,
    onTouchMove: onTouch,
    onTouchEnd: onTouch,

    showControlsOverlay: controlsEnabled && showControlsOverlay,
  }
}
